import { useCallback, useEffect, useRef, useState } from 'react';
import type { ArtworkApiError } from '../services/apiClient';
import { listFiles, type ListBody } from './api';
import { cachedList, rememberList } from './homeCache';
import { anyPending, appendPage, PAGE, type FileRow } from './rows';

/** How long after a list shows a file whose picture is still being made before it is asked again. */
const PENDING_RECHECK_MS = 6_000;

/**
 * One list of files, a page at a time: the scope's grid, a project's, a search's, the
 * recents. `key` says when it is a different list (a new scope, search, project or
 * workspace), which starts it again from the top; `body` null means it is not wanted.
 *
 * If anything it shows has its picture still being made, it asks once more after a few
 * seconds so the fresh thumbnails arrive — once, never a polling loop.
 *
 * `read` asks for another list of files than `artwork.doc.list` — the Trash's, Archived's —
 * paged the same way.
 *
 * The last rows of each list are kept for the tab (`homeCache.ts`): a list asked for again,
 * when Home opens once more, shows them at once and is asked again behind them.
 */
export function useFileList<R extends FileRow = FileRow>(
  body: Omit<ListBody, 'skip' | 'limit'> | null,
  key: string,
  limit: number = PAGE,
  { keep = false, read }: { keep?: boolean; read?: (body: ListBody) => Promise<R[]> } = {},
) {
  const reader = useRef(read);
  reader.current = read;
  const askFor = useCallback((wanted: ListBody) => (reader.current ?? (listFiles as unknown as (body: ListBody) => Promise<R[]>))(wanted), []);
  /** Where this list's rows are kept between visits; null when the list is not wanted. */
  const cacheKey = body ? `${key}#${limit}` : null;
  const cacheKeyNow = useRef(cacheKey);
  cacheKeyNow.current = cacheKey;
  const [rows, setRows] = useState<R[] | null>(() => (cacheKey ? (cachedList<R>(cacheKey)?.rows ?? null) : null));
  /**
   * The rows on screen belong to the list before this one. With `keep`, a new list does not
   * blank the old one while it is asked for: clearing it collapsed the page to a row of grey
   * tiles and then grew it again, and everything above jumped twice on every tab.
   */
  const [stale, setStale] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [more, setMore] = useState(() => (cacheKey ? (cachedList<R>(cacheKey)?.more ?? false) : false));
  const [loadingMore, setLoadingMore] = useState(false);
  /** Bumped to ask the same list again from the top. */
  const [round, setRound] = useState(0);
  const lastKey = useRef<string | null>(null);
  /** The newest list asked for, so an answer to an older one never lands over it. */
  const asked = useRef(0);
  const shown = useRef<R[] | null>(rows);
  const moreNow = useRef(more);
  const bodyNow = useRef(body);
  bodyNow.current = body;
  const recheck = useRef<{ ask: number; timer: ReturnType<typeof setTimeout> | null; done: boolean }>({ ask: 0, timer: null, done: false });

  const show = useCallback((next: R[] | null, nextMore: boolean = moreNow.current) => {
    shown.current = next;
    setRows(next);
    moreNow.current = nextMore;
    setMore(nextMore);
    if (cacheKeyNow.current) rememberList(cacheKeyNow.current, next ? { rows: next, more: nextMore } : null);
  }, []);

  /** After a page lands: if a picture is still coming, ask once more, later. */
  const maybeRecheck = useCallback(
    (ask: number, list: R[]) => {
      const state = recheck.current;
      if (state.ask !== ask || state.done || !anyPending(list)) return;
      state.done = true;
      state.timer = setTimeout(() => {
        const wanted = bodyNow.current;
        if (ask !== asked.current || !wanted) return;
        const count = Math.max(shown.current?.length ?? 0, limit);
        askFor({ ...wanted, limit: count, skip: 0 })
          .then((fresh) => {
            if (ask === asked.current) show(fresh);
          })
          .catch(() => {
            // The pictures stay as they were; the list itself already showed.
          });
      }, PENDING_RECHECK_MS);
    },
    [limit, show, askFor],
  );

  useEffect(() => {
    const ask = ++asked.current;
    if (recheck.current.timer) clearTimeout(recheck.current.timer);
    recheck.current = { ask, timer: null, done: false };
    // A different list starts from nothing; the same one asked again keeps showing until the answer lands.
    // One shown on an earlier visit is drawn at once, and asked again behind it.
    if (lastKey.current !== key) {
      lastKey.current = key;
      const kept = cacheKeyNow.current ? cachedList<R>(cacheKeyNow.current) : undefined;
      if (kept) {
        if (shown.current !== kept.rows) show(kept.rows, kept.more);
        setStale(false);
      } else if (keep && shown.current) setStale(true);
      else show(null, false);
    }
    setError(null);
    const wanted = bodyNow.current;
    if (!wanted) return;
    askFor({ ...wanted, limit, skip: 0 })
      .then((page) => {
        if (ask !== asked.current) return;
        show(page, page.length === limit);
        setStale(false);
        maybeRecheck(ask, page);
      })
      .catch((err: ArtworkApiError) => {
        if (ask !== asked.current) return;
        setError(err.message);
        setStale(false);
        show(null, false);
      });
    // `key` stands for the body: a new key is a new list.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, limit, round]);

  useEffect(
    () => () => {
      if (recheck.current.timer) clearTimeout(recheck.current.timer);
    },
    [],
  );

  const loadMore = useCallback(() => {
    const wanted = bodyNow.current;
    const now = shown.current;
    if (!wanted || !now) return;
    const ask = asked.current;
    setLoadingMore(true);
    askFor({ ...wanted, limit, skip: now.length })
      .then((page) => {
        if (ask !== asked.current) return;
        const next = appendPage(shown.current ?? [], page);
        show(next, page.length === limit);
        maybeRecheck(ask, page);
      })
      .catch((err: ArtworkApiError) => {
        if (ask === asked.current) setError(err.message);
      })
      .finally(() => {
        if (ask === asked.current) setLoadingMore(false);
      });
  }, [limit, maybeRecheck, show, askFor]);

  /** A file changed elsewhere (moved): the new row, or null to take it out of this list. */
  const patch = useCallback(
    (id: string, row: R | null) => {
      const now = shown.current;
      if (!now || !now.some((one) => one.id === id)) return;
      show(row ? now.map((one) => (one.id === id ? row : one)) : now.filter((one) => one.id !== id));
    },
    [show],
  );

  /** The same list again, from the top — a short shelf refilled after a file left it. */
  const reload = useCallback(() => setRound((n) => n + 1), []);

  return { rows, stale, error, more, loadingMore, loadMore, patch, reload };
}
